import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
// hooks
import { useActions } from "../../../common/hooks/useActions";
import { useDidMountEffect } from "../../../common/hooks/useDidMountEffect";
// icons
import { dateIcon, timeIcon } from "../../../common/vector";
// helpers
import { hoursUpdate } from "../../../components/AppTimeComponent/helpers/updateHours";
import { updateMinutes } from "../../../components/AppTimeComponent/helpers/updateMinutes";
import { checkIsItInFuture } from "../../../common/utils/helpers/checkIsItInFuture";
import { addLeadingZero } from "../../../common/utils/helpers/appLeadingZero";
import { isUberVehicleAvailable } from "./helpers/isUberVehicleAvailable";
import { pushTransportBookMarketplaceContext } from "../../../common/utils/analytics/pushTransportBookMarketplaceContext";
import { useCookies } from "react-cookie";
// Components
import UberRowAction from "./UberRowAction";
import UberTypeOfCars from "./UberTypeOfCars";
import UberSummary from "./UberSummary";
import AppDatePickerCustom from "../../../components/AppDatePickerCustom";
import AppTimeComponent from "../../../components/AppTimeComponent";
import AppButton from "../../../components/ui/AppButton";
import CustomGoogleAutocomplete from "../../../components/CustomGoogleAutocomplete";
import PaymentModal from "../../../components/Payment/PaymentModal";
import TransportProviderHeader from "../../../components/TransportProviderContent/TransportProviderHeader";
// actions
import { googleSearchCreators } from "../../../common/store/modules/googleSearchModule";
import { uberActionCreators } from "./uberModule";

interface GooglePlaceResult {
  value?: {
    structured_formatting?: {
      secondary_text?: string;
      main_text?: string;
    };
    place_id?: string;
  };
}

export default function Uber() {
  const { t } = useTranslation();
  const [cookies] = useCookies(["cookieAccepted"]);
  const navigate = useNavigate();

  const language = localStorage.getItem("i18nextLng");

  const now = new Date();
  now.setMinutes(now.getMinutes() + 5); // adding 5 minutes

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [uberHours, setUberHours] = useState(now.getHours());
  const [uberMinutes, setUberMinutes] = useState(now.getMinutes());
  const [notAvailableMessage, setNotAvailableMessage] = useState(t("transport.noAvailableVehicle"));
  const [confirmOrderModalVisible, setConfirmOrderModalVisible] = useState(false);

  // Routes
  const [selectedFromStation, setSelectedFromStation] = useState<GooglePlaceResult | null>(null);
  const [selectedToStation, setSelectedToStation] = useState<GooglePlaceResult | null>(null);
  // set is pre-request ride button is valid to be clicked, if time is valid (current or in the future)
  const [isRequestTimeValid, setIsRequestTimeValid] = useState(true);

  // actions
  const getGoogleGeocoding = useActions(googleSearchCreators?.getGoogleGeocoding, []);
  const setIsValidGoogleFromToAddress = useActions(googleSearchCreators?.setIsValidGoogleFromToAddress, []);
  const resetGoogleGeocodingFromToState = useActions(googleSearchCreators?.resetGoogleGeocodingFromToState, []);
  const getUberInitial = useActions(uberActionCreators?.getUberInitial, []);
  const setUberTypeOfCar = useActions(uberActionCreators?.setUberTypeOfCar, []);
  const resetUberState = useActions(uberActionCreators?.resetUberState, []);
  const getUberEstimation = useActions(uberActionCreators?.getUberEstimation, []);
  const buyUberTicket = useActions(uberActionCreators?.buyUberTicketAction, []);
  const toggleUberPaymentModal = useActions(uberActionCreators?.toggleUberPaymentModalAction, []);
  const toggleUberBookingError = useActions(uberActionCreators?.toggleUberBookingErrorAction, []);
  const setUberEstimationErrorMessage = useActions(uberActionCreators?.setUberEstimationErrorMessageAction, []);
  const handleBookARide = useActions(uberActionCreators?.handleBookARideAction, []);

  // states
  const tenantInfo = useSelector((state: any) => state.tenant.tenantInfo);
  const platformType = useSelector((state: any) => state.global.platformType);
  const uberInitial = useSelector((state: any) => state.uber.uberInitial);
  const uberInitialItem = useSelector((state: any) => state.uber.uberInitialItem);
  const isLoadingGeocodingDeparture = useSelector((state: any) => state.googleSearch.isLoadingGeocodingDeparture);
  const isLoadingGeocodingDestination = useSelector((state: any) => state.googleSearch.isLoadingGeocodingDestination);
  const geocodingDeparture = useSelector((state: any) => state.googleSearch.geocodingDeparture);
  const geocodingDestination = useSelector((state: any) => state.googleSearch.geocodingDestination);
  const isValidDepartureAddress = useSelector((state: any) => state.googleSearch.isValidDepartureAddress);
  const isValidDestinationAddress = useSelector((state: any) => state.googleSearch.isValidDestinationAddress);
  const uberVehicle = useSelector((state: any) => state.uber.uberVehicle);
  const estimationAvailable = useSelector((state: any) => state.uber.estimationAvailable);
  const uberEstimationError = useSelector((state: any) => state.uber.uberEstimationError);
  const showUberPaymentModal = useSelector((state: any) => state.uber.showUberPaymentModal);
  const uberBookingResponseData = useSelector((state: any) => state.uber.uberBookingResponseData);
  const googlePlacesApiKey = useSelector((state: any) => state.global.googlePlacesApiKey);

  useEffect(() => {
    if (tenantInfo) {
      getUberInitial();
    }
  }, [tenantInfo]);

  // If the language has been changed, we should update the URL
  useDidMountEffect(() => {
    navigate(`/${language}/transport/uber`, { replace: true });
  }, [language]);

  // reset on unmount
  useEffect(() => {
    return () => {
      resetGoogleGeocodingFromToState();
      resetUberState();
    };
  }, []);

  useEffect(() => {
    // Set initial Uber type of car (first available)
    const firstTypeOfCarAvailable = uberVehicle?.length > 0 && uberVehicle.find((item: any) => item.price !== null);
    if (firstTypeOfCarAvailable) {
      setUberTypeOfCar(firstTypeOfCarAvailable);
    }
  }, [uberVehicle]);

  useEffect(() => {
    if (tenantInfo && uberInitialItem) {
      setSelectedFromStation({
        value: {
          structured_formatting: {
            main_text: tenantInfo?.name,
            secondary_text: uberInitialItem?.uber_settings?.address?.formatted_address
          },
          place_id: ""
        }
      });

      setIsValidGoogleFromToAddress("departure", true);
    }
  }, [tenantInfo, uberInitialItem]);

  // Check if selected date is in the past. This effect should fire on every date/time changes
  useEffect(() => {
    setIsRequestTimeValid(checkIsItInFuture(selectedDate, uberHours, uberMinutes, 0));
  }, [selectedDate, uberHours, uberMinutes]);

  // Handle ROUTE selecting departure (from) address
  useDidMountEffect(() => {
    const isValidAddress = geocodingDeparture?.value?.isValidAddress;
    setIsValidGoogleFromToAddress("departure", isValidAddress);
    updateDepartureAddress(geocodingDeparture);
  }, [geocodingDeparture]);

  // Handle ROUTE selecting destination (to) address
  useDidMountEffect(() => {
    const isValidAddress = geocodingDestination?.value?.isValidAddress;
    setIsValidGoogleFromToAddress("destination", isValidAddress);
    updateDestinationAddress(geocodingDestination);
  }, [geocodingDestination]);

  const shouldEnablePreRequestRideBtn = (): boolean => {
    const areRoutesValid: boolean = isValidDepartureAddress && isValidDestinationAddress;
    return areRoutesValid && isRequestTimeValid;
  };

  const renderPrebookErrorMsg = () => {
    let errors: Array<any> = [];

    if (selectedFromStation && !isValidDepartureAddress) {
      errors.push({
        errorMessage: t("transport.departureAddressInvalid")
      });
    }
    if (selectedToStation && !isValidDestinationAddress) {
      errors.push({
        errorMessage: t("transport.destinationAddressInvalid")
      });
    }

    if (!isValidDestinationAddress || !isValidDepartureAddress) {
      errors.push({
        errorMessage: t("transport.requiredAddresses"),
        info: true
      });
    }

    if (estimationAvailable && !isUberVehicleAvailable(uberVehicle)) {
      errors.push({ errorMessage: notAvailableMessage });
    }

    if (!checkIsItInFuture(selectedDate, uberHours, uberMinutes, 0)) {
      errors.push({
        errorMessage: t("transport.timeErrorMsg")
      });
    }

    if (uberEstimationError) {
      errors.push({
        errorMessage: t("transport.prebookCustomError")
      });
    }

    return errors?.map((error: any, i: number) =>
      error?.errorMessage ? (
        <div key={i} className={`uber__error-msg ${error?.info ? "uber__error-msg--info" : ""}`}>
          {error?.field && `${error?.field} -`} {error?.errorMessage}
        </div>
      ) : null
    );
  };

  const handlePreRequestRideBtn = () => {
    setUberEstimationErrorMessage([]);
    setNotAvailableMessage(t("transport.noAvailableVehicle"));
    const isTimeValidOnClick: boolean = checkIsItInFuture(selectedDate, uberHours, uberMinutes, 0);
    if (isTimeValidOnClick) {
      // handle getUberEstimation if time is valid
      let time;
      let date;
      time = addLeadingZero(uberHours) + ":" + addLeadingZero(uberMinutes);
      date = format(new Date(selectedDate), "yyyy-MM-dd");

      getUberEstimation({
        time,
        date,
        departureAddress: selectedFromStation?.value?.structured_formatting?.secondary_text,
        departureAddressId: selectedFromStation?.value?.place_id ? selectedFromStation?.value?.place_id : null,
        destinationAddress: selectedToStation?.value?.structured_formatting?.secondary_text,
        destinationAddressId: selectedToStation?.value?.place_id
      });
    } else {
      // If isTimeValidOnClick is not true, we should update state and set it to isRequestTimeValid to false
      setIsRequestTimeValid(false);
    }
  };

  // Handling from-to methods:

  const updateDepartureAddress = (address: any) => {
    setSelectedFromStation(address);
  };

  const updateDestinationAddress = (address: any) => {
    setSelectedToStation(address);
  };

  const handleDepartureAddress = (address: any, action: any) => {
    if (action.action === "clear") return;
    handleGeocoding(address, "departure");
  };

  const handleDestinationAddress = (address: any, action: any) => {
    if (action.action === "clear") return;
    handleGeocoding(address, "destination");
  };

  const onClearRouteInput = (input: any) => {
    const isDepartureInput = input === "first_input";
    if (isDepartureInput) {
      setIsValidGoogleFromToAddress("departure", false);
      updateDepartureAddress(null);
      setNotAvailableMessage("");
      toggleUberBookingError(false);
    } else {
      // destination is mandatory, so when user clicks on X we should set destination validation on false/not-valid
      setIsValidGoogleFromToAddress("destination", false);
      updateDestinationAddress(null);
      setNotAvailableMessage("");
      toggleUberBookingError(false);
    }
  };

  const handleGeocoding = (address: any, route: any) => {
    const shouldUseLatLngValidation = true;
    getGoogleGeocoding(address?.value?.place_id, route, shouldUseLatLngValidation);
  };

  // END OF Handling from-to methods:

  const handleUberTypeOfCar = (vehicle: any) => {
    setUberTypeOfCar(vehicle);
  };

  const getDisabledStyleForTicketsForm = () => {
    if (estimationAvailable && isUberVehicleAvailable(uberVehicle)) {
      return { opacity: 0.4, pointerEvents: "none" };
    }
  };

  const handleBuyUberTicket = () => {
    pushTransportBookMarketplaceContext({
      cookieAccepted: cookies.cookieAccepted,
      platformType,
      tenantInfo,
      offerCode: uberInitialItem?.event_code ?? null
    });

    buyUberTicket(
      {
        start_date: Math.round(+new Date(selectedDate) / 1000), // get unix timestamp
        timeslot: `${uberHours}:${uberMinutes}`,
        departureAddress: selectedFromStation?.value?.structured_formatting?.secondary_text,
        departureAddressId: selectedFromStation?.value?.place_id ? selectedFromStation?.value?.place_id : null,
        destinationAddress: selectedToStation?.value?.structured_formatting?.secondary_text,
        destinationAddressId: selectedToStation?.value?.place_id
      },
      navigate,
      setConfirmOrderModalVisible
    );
  };
  const handleBookARideBtn = () => {
    pushTransportBookMarketplaceContext({
      cookieAccepted: cookies.cookieAccepted,
      platformType,
      tenantInfo,
      offerCode: uberInitialItem?.event_code ?? null
    });

    handleBookARide(setConfirmOrderModalVisible);
  };

  return (
    <Grid
      className="uber grid-box"
      item
      sx={{ flexDirection: "column", flex: 1, margin: 0, padding: 0 }}
      mt={{ xs: 1, sm: 3 }}
      mb={{ xs: 4, sm: 5 }}
    >
      <>
        <TransportProviderHeader
          title={uberInitial?.provider_title ?? uberInitial?.title}
          titleClassName="uber__title"
          shortDescription={uberInitial?.provider_description}
          providerLogo={uberInitial?.provider_logo}
        />

        <Grid container>
          <Grid
            item
            sx={{ display: "flex", flexDirection: "column", ...getDisabledStyleForTicketsForm() }}
            md={6}
            xs={12}
          >
            <UberRowAction
              label={t("transport.departureDate")}
              icon={dateIcon?.default}
              actionComponent={
                <Grid item width={{ xs: 200, sm: 220, md: 260 }}>
                  <AppDatePickerCustom
                    selectedDate={selectedDate}
                    setSelectedDate={date => {
                      setSelectedDate(date ?? new Date());
                      setNotAvailableMessage("");
                      toggleUberBookingError(false);
                    }}
                    minDate={new Date()}
                  />
                </Grid>
              }
            />
            <UberRowAction
              label={t("transport.departureTime")}
              icon={timeIcon?.default}
              actionComponent={
                <Grid item width={{ xs: 200, sm: 220, md: 260 }}>
                  <AppTimeComponent
                    hours={uberHours}
                    minutes={uberMinutes}
                    handleHoursChange={(change: string, amount: number) => {
                      hoursUpdate(setUberHours, change, amount, uberHours);
                      setNotAvailableMessage("");
                      toggleUberBookingError(false);
                    }}
                    handleMinutesChange={(change: string, amount: number) => {
                      updateMinutes(setUberMinutes, setUberHours, change, amount, uberHours, uberMinutes);
                      setNotAvailableMessage("");
                      toggleUberBookingError(false);
                    }}
                    timeBoxSize="w-80"
                  />
                </Grid>
              }
            />
          </Grid>
          <Grid item md={6} xs={12} mt={{ sm: 6, xs: 6, md: 0 }} position={"relative"}>
            <div className="vertical-divider" />
            <Box pl={{ xs: 2, sm: 6 }} pr={{ xs: 2, sm: 4 }}>
              <Box sx={{ ...getDisabledStyleForTicketsForm() }}>
                {googlePlacesApiKey && (
                  <CustomGoogleAutocomplete
                    apiKey={googlePlacesApiKey}
                    inputTestIdOfFirstInput="uber-departure"
                    inputTestIdOfSecondInput="uber-destination"
                    valueOfFirstInput={selectedFromStation}
                    valueOfSecondInput={selectedToStation}
                    setValueOfFirstInput={handleDepartureAddress}
                    setValueOfSecondInput={handleDestinationAddress}
                    onClear={onClearRouteInput}
                    isLoadingFirstInput={isLoadingGeocodingDeparture}
                    isLoadingSecondInput={isLoadingGeocodingDestination}
                    showStar={true}
                    location={{
                      lat: 0,
                      lng: 0
                    }}
                  />
                )}
                <Box mt={1}>{renderPrebookErrorMsg()}</Box>
                <Grid item xs={12} sm={4} sx={{ opacity: shouldEnablePreRequestRideBtn() ? 1 : 0.8, mt: 3 }}>
                  <AppButton
                    label={t("transport.preRequestRide")}
                    onClick={handlePreRequestRideBtn}
                    disableRipple={true}
                    customBackgroundColor={tenantInfo?.style?.button?.color}
                    customActiveColor={tenantInfo?.style?.button?.active}
                    customHoverColor={tenantInfo?.style?.button?.hover}
                    customClickColor={tenantInfo?.style?.button?.click}
                    fontSize="14px"
                    disabled={!shouldEnablePreRequestRideBtn()}
                  />
                </Grid>
              </Box>

              <Box mt={4}>
                <Box className="uber-quick-selection-title">{t("transport.uberSelectionTitle")}</Box>
                {uberVehicle?.length > 0 && (
                  <UberTypeOfCars
                    handleUberTypeOfCar={handleUberTypeOfCar}
                    isDisabled={!estimationAvailable || !isUberVehicleAvailable(uberVehicle)}
                  />
                )}
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} mt={{ xs: 4, sm: 6 }} ml={{ xs: 2, sm: 5, md: 6 }} mr={{ xs: 2, sm: 5, md: 6 }}>
            {estimationAvailable && isUberVehicleAvailable(uberVehicle) && (
              <UberSummary
                confirmOrderModalVisible={confirmOrderModalVisible}
                setConfirmOrderModalVisible={setConfirmOrderModalVisible}
                onBookARideBtn={handleBookARideBtn}
                onBuyTicket={handleBuyUberTicket}
                selectedFromStation={selectedFromStation}
                selectedToStation={selectedToStation}
                setNotAvailableMessage={setNotAvailableMessage}
                cancellationPolicy={uberInitialItem?.cancellation_policy || uberInitial?.cancellation_policy}
              />
            )}
          </Grid>
        </Grid>
      </>

      {showUberPaymentModal && (
        <PaymentModal
          isOpen={showUberPaymentModal}
          bookingResponseData={uberBookingResponseData}
          togglePaymentModal={toggleUberPaymentModal}
          redirectUrl="transport/uber/confirmation"
        />
      )}
    </Grid>
  );
}
